import * as React from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useController } from "react-hook-form";
import { NumberInput } from "@/components/ui/NumberInput";
import { DataTableCellErrorTooltip } from "./DataTableCellErrorTooltip";

interface DataTableCellInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  /** Applied to the wrapper div (e.g. for ::after highlight—inputs can't display pseudo-elements) */
  wrapperClassName?: string;
}

const DataTableCellInput = React.forwardRef<
  HTMLInputElement,
  DataTableCellInputProps
>((props, ref) => {
  const controller = useController({ name: props.name as string });
  const defaultStyle = "h-full rounded-sm focus-visible:ring-offset-0";
  const errorStyle = "cell-field-error";

  const { className, wrapperClassName, type, ...restProps } = props;

  const isNumber = type === "number";
  const InternalInput = isNumber ? NumberInput : Input;

  const error = controller.fieldState.error;
  const errorMessage = error?.message?.trim();

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-1 h-full py-[2px] px-2 relative min-w-0",
        wrapperClassName,
      )}
    >
      <InternalInput
        ref={ref}
        {...restProps}
        {...(isNumber ? {} : { type })}
        className={cn(
          defaultStyle,
          "min-w-0 flex-1",
          {
            [errorStyle]: error,
          },
          className,
        )}
      />
      {error && errorMessage && (
        <DataTableCellErrorTooltip message={errorMessage} />
      )}
    </div>
  );
});

DataTableCellInput.displayName = "DataTableCellInput";

export default DataTableCellInput;
